'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, Check } from 'lucide-react';

export default function RegisterPage() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordStrength = password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, { fullName });
      router.push('/student-dashboard');
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--primary)' }}>
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center relative overflow-hidden px-16"
        style={{ background: 'linear-gradient(160deg, #1a2b22 0%, #22382c 60%, #141e18 100%)' }}>
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)' }} />
        <div className="absolute right-0 top-0 bottom-0 w-px" style={{ background: 'linear-gradient(180deg, transparent, rgba(201,168,76,0.2), transparent)' }} />

        <div className="flex flex-col items-center text-center max-w-sm">
          <div className="relative mb-10" style={{ width: 220, height: 220 }}>
            <Image
              src="/assets/images/REUSA_PNG_LOGO__4_-1781810452969.png"
              alt="REUSA Official Crest"
              fill
              className="object-contain drop-shadow-2xl"
              priority
              sizes="220px"
            />
          </div>
          <div className="w-12 h-px mb-8" style={{ background: 'linear-gradient(90deg, transparent, #c9a84c, transparent)' }} />
          <p className="font-sans text-white/40 text-xs tracking-widest uppercase mb-3">Accreditation</p>
          <div className="space-y-2">
            {['NQF Accredited', 'SAQA Registered', 'CPD Recognised', 'Industry Endorsed'].map((item) => (
              <div key={item} className="flex items-center gap-2 justify-center">
                <Check size={12} className="text-accent/70" />
                <span className="font-sans text-white/40 text-xs tracking-wide">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 bg-white overflow-y-auto">
        {/* Top-left logo — links back to homepage */}
        <div className="absolute top-0 right-0 left-0 lg:left-1/2 flex items-center px-6 h-16 pointer-events-none">
          <Link href="/" className="pointer-events-auto flex items-center gap-2 hover:opacity-80 transition-opacity" aria-label="REUSA Home">
            <Image
              src="/assets/images/REUSA_PNG_LOGO__4_-1781810452969.png"
              alt="REUSA logo"
              width={88}
              height={32}
              className="object-contain"
              priority
            />
          </Link>
        </div>

        <div className="lg:hidden mb-10 relative" style={{ width: 96, height: 96 }}>
          <Image
            src="/assets/images/REUSA_PNG_LOGO__4_-1781810452969.png"
            alt="REUSA Crest"
            fill
            className="object-contain"
            sizes="96px"
          />
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h1 className="font-serif font-bold text-3xl text-primary mb-2 leading-tight">
              Create Account
            </h1>
            <p className="font-sans text-sm text-muted-foreground">
              Begin your journey with REUSA
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block font-sans text-xs font-semibold text-foreground/70 tracking-widest uppercase mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Your full name"
                className="w-full px-4 py-3.5 bg-secondary border border-border rounded-xl font-sans text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label className="block font-sans text-xs font-semibold text-foreground/70 tracking-widest uppercase mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="w-full px-4 py-3.5 bg-secondary border border-border rounded-xl font-sans text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label className="block font-sans text-xs font-semibold text-foreground/70 tracking-widest uppercase mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Min. 8 characters"
                  className="w-full px-4 py-3.5 bg-secondary border border-border rounded-xl font-sans text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {password.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <div className={`h-1 flex-1 rounded-full transition-colors ${passwordStrength ? 'bg-green-500' : 'bg-amber-400'}`} />
                  <span className={`font-sans text-xs ${passwordStrength ? 'text-green-600' : 'text-amber-600'}`}>
                    {passwordStrength ? 'Strong' : 'Weak'}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block font-sans text-xs font-semibold text-foreground/70 tracking-widest uppercase mb-2">
                Confirm Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Repeat password"
                className="w-full px-4 py-3.5 bg-secondary border border-border rounded-xl font-sans text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="font-sans text-xs text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl font-sans font-semibold text-sm tracking-wide transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: loading ? '#c9a84c99' : 'linear-gradient(135deg, #c9a84c, #b8922e)', color: '#1a2b22' }}
            >
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="font-sans text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="font-semibold text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link href="/" className="font-sans text-xs text-muted-foreground hover:text-primary transition-colors tracking-wide">
              ← Return to main site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
